import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { CalendarHeart, Check, MapPin, Minus, Plus, Trash2, X } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { api, ApiError, call, type Schemas } from '../../api/client';
import { useAction, useFamilyActivities, useFamilyActivity } from '../../api/queries';
import { useHousehold } from '../../api/session';
import { Badge, Button, Card, EmptyState, ErrorText, Field, Input, Modal, PageHeader, Spinner, Textarea } from '../../components/ui';
import { formatWeekday } from '../../lib/format';

type Activity = Schemas['FamilyActivityResponse'];
type Option = Activity['options'][number];
type Answer = 'YES' | 'MAYBE' | 'NO';

/** "Sat, Oct 10 · 09:30" (or the whole day). */
export const optionLabel = (o: Pick<Option, 'onDate' | 'startTime'>) => `${formatWeekday(o.onDate)}${o.startTime ? ` · ${o.startTime}` : ''}`;
const chosen = (a: Activity) => a.options.find((o) => o.id === a.confirmedOptionId);
const today = () => new Date().toLocaleDateString('en-CA');

function StatusBadge({ a }: { a: Activity }) {
  if (a.status === 'CONFIRMED') return <Badge tone="accent"><Check size={11} /> {optionLabel(chosen(a)!)}</Badge>;
  if (a.status === 'CANCELLED') return <Badge tone="danger">Called off</Badge>;
  return <Badge tone="info">Picking a day</Badge>;
}

export function ActivitiesPage() {
  const [scope, setScope] = useState<'upcoming' | 'past'>('upcoming');
  const list = useFamilyActivities(scope).data;
  const [creating, setCreating] = useState(false);
  return (
    <>
      <PageHeader
        title="Family activities"
        subtitle="Suggest something to do together, offer a few days, and see which works for everyone."
        actions={<Button onClick={() => setCreating(true)}><Plus size={16} /> Suggest</Button>}
      />
      <div className="mb-4 flex gap-2 text-sm">
        {(['upcoming', 'past'] as const).map((s) => (
          <button key={s} type="button" onClick={() => setScope(s)} className={`rounded-lg px-3 py-1 ${scope === s ? 'bg-[var(--accent)] text-white' : 'bg-[var(--surface-2)]'}`}>
            {s === 'upcoming' ? 'Upcoming' : 'Past'}
          </button>
        ))}
      </div>
      {!list ? <Spinner /> : list.length === 0 ? (
        <EmptyState title={scope === 'upcoming' ? 'Nothing planned yet' : 'No past activities'}>
          {scope === 'upcoming' ? 'A hike, a movie night, a trip to the zoo… suggest one and offer a few days.' : null}
        </EmptyState>
      ) : (
        <div className="space-y-2">
          {list.map((a) => (
            <Link key={a.id} to="/activities/$activityId" params={{ activityId: a.id }}>
              <Card className="flex items-center gap-3 hover:bg-[var(--surface-2)]">
                <CalendarHeart size={18} className="shrink-0 text-[var(--muted)]" />
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium">{a.title}</p>
                  <p className="text-xs text-[var(--muted)]">
                    {a.status === 'PROPOSED' ? `${a.options.length} ${a.options.length === 1 ? 'day' : 'days'} offered · suggested by ${a.proposedBy.displayName}` : `Suggested by ${a.proposedBy.displayName}`}
                    {a.location ? ` · ${a.location}` : ''}
                  </p>
                </div>
                <StatusBadge a={a} />
              </Card>
            </Link>
          ))}
        </div>
      )}
      {creating ? <ActivityFormDialog onClose={() => setCreating(false)} /> : null}
    </>
  );
}

function ActivityFormDialog({ onClose, activity }: { onClose(): void; activity?: Activity }) {
  const { familyId } = useHousehold();
  const navigate = useNavigate();
  const [options, setOptions] = useState<{ onDate: string; startTime: string }[]>([{ onDate: today(), startTime: '' }]);
  const save = useAction(
    (f: FormData) => {
      const details = {
        title: String(f.get('title')),
        description: (f.get('description') as string) || null,
        location: (f.get('location') as string) || null,
      };
      if (activity) {
        return call(api.PATCH('/api/v1/families/{familyId}/activities/{activityId}', { params: { path: { familyId, activityId: activity.id } }, body: { expectedVersion: activity.version, ...details } }));
      }
      return call(
        api.POST('/api/v1/families/{familyId}/activities', {
          params: { path: { familyId }, header: { 'Idempotency-Key': crypto.randomUUID() } },
          body: { ...details, options: options.map((o) => ({ onDate: o.onDate, startTime: o.startTime || null })) },
        }),
      );
    },
    { success: activity ? 'Saved' : 'Suggestion sent' },
  );
  const set = (i: number, patch: Partial<(typeof options)[number]>) => setOptions((cur) => cur.map((o, j) => (j === i ? { ...o, ...patch } : o)));
  const optionsError = save.error instanceof ApiError ? save.error.problem.errors?.filter((e) => e.field.startsWith('options')).map((e) => e.message).join('; ') : undefined;
  return (
    <Modal open onOpenChange={onClose} title={activity ? 'Edit activity' : 'Suggest an activity'}>
      <form
        className="space-y-4"
        onSubmit={(e: FormEvent<HTMLFormElement>) => {
          e.preventDefault();
          save.mutate(new FormData(e.currentTarget), {
            onSuccess: (a) => {
              onClose();
              if (!activity) void navigate({ to: '/activities/$activityId', params: { activityId: a.id } });
            },
          });
        }}
      >
        <Field label="What should we do?">{(id) => <Input id={id} name="title" required maxLength={200} defaultValue={activity?.title} placeholder="Hike at Bear Lake" autoFocus />}</Field>
        <Field label="Where (optional)">{(id) => <Input id={id} name="location" maxLength={200} defaultValue={activity?.location ?? ''} />}</Field>
        <Field label="Details (optional)">{(id) => <Textarea id={id} name="description" maxLength={2000} defaultValue={activity?.description ?? ''} />}</Field>
        {!activity ? (
          <div className="space-y-2">
            <p className="text-sm font-medium">Days to choose from</p>
            {options.map((o, i) => (
              <div key={i} className="flex items-center gap-2">
                <Input aria-label={`Day ${i + 1}`} type="date" required min={today()} value={o.onDate} onChange={(e) => set(i, { onDate: e.target.value })} />
                <Input aria-label={`Time ${i + 1} (optional)`} type="time" value={o.startTime} onChange={(e) => set(i, { startTime: e.target.value })} />
                {options.length > 1 ? (
                  <Button type="button" variant="ghost" aria-label={`Remove day ${i + 1}`} onClick={() => setOptions((cur) => cur.filter((_, j) => j !== i))}><Trash2 size={16} /></Button>
                ) : null}
              </div>
            ))}
            {options.length < 5 ? (
              <Button type="button" variant="secondary" size="sm" onClick={() => setOptions((cur) => [...cur, { onDate: cur[cur.length - 1]?.onDate ?? today(), startTime: '' }])}>
                <Plus size={14} /> Another day
              </Button>
            ) : null}
            <p className="text-xs text-[var(--muted)]">Leave the time empty for "any time that day". Everyone says which days work, then you pick one.</p>
            {optionsError ? <p className="text-sm text-[var(--danger)]">{optionsError}</p> : null}
          </div>
        ) : null}
        {save.error && !(save.error instanceof ApiError && save.error.code === 'VALIDATION_FAILED') ? <ErrorText error={save.error} /> : null}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
          <Button type="submit" busy={save.isPending}>{activity ? 'Save' : 'Send suggestion'}</Button>
        </div>
      </form>
    </Modal>
  );
}

const ANSWER_STYLE: Record<Answer, string> = {
  YES: 'bg-[var(--accent)] text-white',
  MAYBE: 'bg-[var(--warn)] text-white',
  NO: 'bg-[var(--surface-2)] text-[var(--muted)] line-through',
};
const ANSWER_ICON = { YES: Check, MAYBE: Minus, NO: X } as const;
const NEXT: Record<string, Answer | null> = { none: 'YES', YES: 'MAYBE', MAYBE: 'NO', NO: null };

export function ActivityDetailPage() {
  const { activityId } = useParams({ from: '/app/activities/$activityId' });
  const { familyId, memberId } = useHousehold();
  const a = useFamilyActivity(activityId).data;
  const [editing, setEditing] = useState(false);
  const answer = useAction((v: { memberId: string; optionId: string; answer: Answer | null }) =>
    call(api.PUT('/api/v1/families/{familyId}/activities/{activityId}/answers/{memberId}', {
      params: { path: { familyId, activityId, memberId: v.memberId } },
      body: { answers: [{ optionId: v.optionId, answer: v.answer }] },
    })),
  );
  const confirm = useAction(
    (v: { optionId: string; version: number }) =>
      call(api.POST('/api/v1/families/{familyId}/activities/{activityId}/confirm', { params: { path: { familyId, activityId } }, body: { optionId: v.optionId, expectedVersion: v.version } })),
    { success: "It's on! Everyone was told." },
  );
  const cancel = useAction(
    (version: number) => call(api.POST('/api/v1/families/{familyId}/activities/{activityId}/cancel', { params: { path: { familyId, activityId } }, body: { expectedVersion: version } })),
    { success: 'Called off' },
  );
  if (!a) return <Spinner />;
  const can = (x: string) => a.allowedActions.includes(x as never);
  const mayAnswerFor = (p: Activity['people'][number]) => can('ANSWER') && (p.memberId === memberId || p.role === 'CHILD');
  const answerOf = (o: Option, who: string) => o.answers.find((x) => x.memberId === who)?.answer as Answer | undefined;
  const best = Math.max(...a.options.map((o) => o.yes));

  return (
    <>
      <PageHeader
        title={a.title}
        subtitle={
          <span className="flex flex-wrap items-center gap-1.5">
            <StatusBadge a={a} />
            <span className="text-xs text-[var(--muted)]">Suggested by {a.proposedBy.memberId === memberId ? 'you' : a.proposedBy.displayName}</span>
            {a.location ? <span className="flex items-center gap-1 text-xs text-[var(--muted)]"><MapPin size={12} /> {a.location}</span> : null}
          </span>
        }
        actions={
          <div className="flex gap-2">
            {can('EDIT') ? <Button variant="secondary" onClick={() => setEditing(true)}>Edit</Button> : null}
            {can('CANCEL') ? <Button variant="ghost" busy={cancel.isPending} onClick={() => cancel.mutate(a.version)}>Call off</Button> : null}
          </div>
        }
      />
      {a.description ? <p className="mb-4 whitespace-pre-line text-sm">{a.description}</p> : null}
      <Card className="overflow-x-auto p-0">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[var(--border)] text-left">
              <th className="p-3 font-medium">Day</th>
              {a.people.map((p) => <th key={p.memberId} className="p-3 text-center font-medium">{p.memberId === memberId ? 'You' : p.displayName}</th>)}
              <th className="p-3 text-center font-medium">Yes</th>
              {can('CONFIRM') ? <th className="p-3" /> : null}
            </tr>
          </thead>
          <tbody>
            {a.options.map((o) => (
              <tr key={o.id} className={`border-b border-[var(--border)] last:border-0 ${o.id === a.confirmedOptionId ? 'bg-[var(--surface-2)]' : ''}`}>
                <td className="whitespace-nowrap p-3 font-medium">{optionLabel(o)}{o.id === a.confirmedOptionId ? ' ✓' : ''}</td>
                {a.people.map((p) => {
                  const current = answerOf(o, p.memberId);
                  const Icon = current ? ANSWER_ICON[current] : null;
                  const label = `${p.memberId === memberId ? 'Your' : `${p.displayName}'s`} answer for ${optionLabel(o)}: ${current?.toLowerCase() ?? 'not answered'}`;
                  return (
                    <td key={p.memberId} className="p-2 text-center">
                      <button
                        type="button"
                        aria-label={mayAnswerFor(p) ? `${label}. Change it.` : label}
                        title={mayAnswerFor(p) ? 'Tap to change: yes → maybe → no → clear' : undefined}
                        disabled={!mayAnswerFor(p) || answer.isPending}
                        onClick={() => answer.mutate({ memberId: p.memberId, optionId: o.id, answer: NEXT[current ?? 'none']! })}
                        className={`inline-flex h-9 w-14 items-center justify-center gap-1 rounded-lg text-xs ${current ? ANSWER_STYLE[current] : 'border border-dashed border-[var(--border)] text-[var(--muted)]'} ${mayAnswerFor(p) ? 'cursor-pointer' : 'cursor-default opacity-90'}`}
                      >
                        {Icon ? <Icon size={12} /> : null}
                        {current ? current.charAt(0) + current.slice(1).toLowerCase() : mayAnswerFor(p) ? 'Answer' : '–'}
                      </button>
                    </td>
                  );
                })}
                <td className={`p-3 text-center ${o.yes === best && best > 0 ? 'font-semibold text-[var(--accent)]' : ''}`}>{o.yes}{o.maybe ? <span className="text-xs text-[var(--muted)]"> +{o.maybe}?</span> : null}</td>
                {can('CONFIRM') ? (
                  <td className="p-2 text-right">
                    <Button size="sm" busy={confirm.isPending} onClick={() => confirm.mutate({ optionId: o.id, version: a.version })}>Pick this day</Button>
                  </td>
                ) : null}
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
      <p className="mt-3 text-xs text-[var(--muted)]">
        {a.status === 'PROPOSED'
          ? `Tap your answer (and the kids') to cycle yes → maybe → no. ${can('CONFIRM') ? 'Pick the day that works best; everyone is told.' : `${a.proposedBy.displayName} picks the day once everyone has answered.`}`
          : a.status === 'CONFIRMED'
          ? 'The day is set. Answers can still change if plans do.'
          : 'This activity was called off.'}
      </p>
      <ErrorText error={answer.error ?? confirm.error} />
      {editing ? <ActivityFormDialog activity={a} onClose={() => setEditing(false)} /> : null}
    </>
  );
}
